import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { POSTERS, downloadPoster, downloadAllPosters } from "./swachhataContent";

/*
 * Renders the two-page campaign brief as a plain note: headings, paragraphs
 * and simple lists. Every visible string is a single text node so the
 * portal's translation extension cannot merge adjacent nodes and drop spaces.
 */

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
    <path d={dir === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Full-screen poster viewer: the poster fills the available height over a
// blurred copy of itself, with prev/next, thumbnails and click-to-zoom.
// Portaled to <body> so no transformed ancestor in the deck can trap it.
function PosterViewer({ index, onIndex, onClose }) {
  const n = POSTERS.length;
  const poster = POSTERS[index];
  const [zoom, setZoom] = useState(null); // null = fit; { x, y } = zoomed at that point (0..1)
  const stageRef = useRef(null);
  const touchX = useRef(null);

  const go = useCallback((d) => {
    setZoom(null);
    onIndex((index + d + n) % n);
  }, [index, n, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" && e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      // Capture phase + stopPropagation keeps the deck from changing slide underneath.
      e.stopPropagation();
      e.preventDefault();
      if (e.key === "Escape") {
        if (zoom) setZoom(null);
        else onClose();
      } else go(e.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [zoom, go, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Centre the zoomed poster on the point that was clicked.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    if (!zoom) { el.scrollTop = 0; el.scrollLeft = 0; return; }
    el.scrollLeft = zoom.x * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = zoom.y * el.scrollHeight - el.clientHeight / 2;
  }, [zoom]);

  const toggleZoom = (e) => {
    e.stopPropagation();
    if (zoom) { setZoom(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setZoom({ x: (e.clientX - r.left) / r.width || 0.5, y: (e.clientY - r.top) / r.height || 0.5 });
  };

  return createPortal(
    <div className={`shs-viewer ${zoom ? "is-zoomed" : ""}`} role="dialog" aria-modal="true" aria-label={poster.title}>
      <img className="shs-viewer__bg" src={poster.src} alt="" aria-hidden="true" key={`bg-${poster.key}`} />

      <div className="shs-viewer__bar">
        <div className="shs-viewer__info">
          <span className="shs-viewer__count">{`Poster ${index + 1} of ${n}`}</span>
          <b className="shs-viewer__title">{poster.title}</b>
        </div>
        <div className="shs-viewer__tools">
          <span className="shs-viewer__hint">{zoom ? "Click the poster to fit it back" : "Click the poster to zoom in"}</span>
          <button type="button" className="shs-viewer__tool" onClick={() => setZoom(zoom ? null : { x: 0.5, y: 0 })}>
            {zoom ? "Fit" : "Zoom"}
          </button>
          <a className="shs-viewer__tool" href={poster.src} target="_blank" rel="noopener noreferrer">Open</a>
          <button type="button" className="shs-viewer__close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
          </button>
        </div>
      </div>

      <div
        className="shs-viewer__stage"
        ref={stageRef}
        onClick={onClose}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (zoom || touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        }}
      >
        <img className="shs-viewer__img" src={poster.src} alt={poster.title} key={poster.key} onClick={toggleZoom} />
      </div>

      <button
        type="button"
        className="shs-viewer__save"
        onClick={() => downloadPoster(poster)}
        aria-label={`Download ${poster.title}`}
      >
        <DownloadIcon />
        Download
      </button>

      <button type="button" className="shs-viewer__nav shs-viewer__nav--prev" aria-label="Previous poster" onClick={() => go(-1)}>
        <Chevron dir="left" />
      </button>
      <button type="button" className="shs-viewer__nav shs-viewer__nav--next" aria-label="Next poster" onClick={() => go(1)}>
        <Chevron dir="right" />
      </button>

      <div className="shs-viewer__thumbs">
        {POSTERS.map((p, i) => (
          <button
            key={p.key}
            type="button"
            className={`shs-viewer__thumb ${i === index ? "is-active" : ""}`}
            aria-label={p.title}
            onClick={() => { setZoom(null); onIndex(i); }}
          >
            <img src={p.src} alt="" />
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}

function Posters() {
  const [open, setOpen] = useState(-1);
  const [saving, setSaving] = useState(0);
  const close = useCallback(() => setOpen(-1), []);
  const saveAll = useCallback(async () => {
    setSaving(1);
    await downloadAllPosters((done) => setSaving(done));
    setTimeout(() => setSaving(0), 1200);
  }, []);
  return (
    <>
      <div className="shs-posterbar">
        <span className="shs-posterbar__text">{`All ${POSTERS.length} campaign creatives — free to download, print and share.`}</span>
        <button type="button" className="shs-posterbar__btn" onClick={saveAll} disabled={saving > 0}>
          {saving > 0 ? `Saving ${saving} of ${POSTERS.length}…` : "Download all creatives"}
        </button>
      </div>
      <div className="shs-posterstrip">
        {POSTERS.map((p, i) => (
          <div key={p.key} className="shs-posterstrip__cell">
            <button type="button" className="shs-posterstrip__item" onClick={() => setOpen(i)}>
              <img src={p.src} alt={p.title} loading="lazy" />
              <span>{p.title}</span>
            </button>
            <button
              type="button"
              className="shs-posterstrip__save"
              onClick={() => downloadPoster(p)}
              aria-label={`Download ${p.title}`}
              title={`Download ${p.title}`}
            >
              <DownloadIcon />
              Download
            </button>
          </div>
        ))}
      </div>
      {open >= 0 && <PosterViewer index={open} onIndex={setOpen} onClose={close} />}
    </>
  );
}

function Block({ block }) {
  if (block.h) return <h3 className="shs-note__h">{block.h}</h3>;
  if (block.p) return <p className="shs-note__p">{block.p}</p>;
  if (block.list) {
    return (
      <ul className="shs-note__list">
        {block.list.map((it) => (
          <li key={it.lead}>
            <b className="shs-note__lead">{it.lead}</b>
            <span>{it.text}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.posters) return <Posters />;
  return null;
}

export default function SlideRenderer({ slide }) {
  return (
    <div className="shs-note">
      <h2 className="shs-note__title">{slide.title}</h2>
      {slide.blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}
